/**
 * F 375 — THE PERSISTENT FILTER ROW.
 *
 * F 369 STOPped with the measurement that produced this: the database view had
 * ONE funnel icon inside a popover, and the filter chips the operator described
 * did not exist anywhere — not in `src/`, not in the shipped bundle. There was
 * no row to promote. So this file builds the row rather than moving it.
 *
 * WHAT THIS IS
 * ------------
 * A dedicated second toolbar beneath the view tabs, in the shape Notion uses:
 * one `Pill` per ACTIVE filter, and a `+ Filter` button that opens the same
 * popover that used to live behind the funnel icon.
 *
 * WHAT THIS IS NOT
 * ----------------
 * It is a VIEW over the existing filter state, and nothing else.
 *
 *   - `filter` is `PropertyFilter` — the same object F 280 persists per view.
 *   - `onChange` is `setPropertyFilter` — the same setter the popover used.
 *   - Every pill and the `+ Filter` button mutate that one object.
 *
 * There is no second filter store, no local copy, no derived "which filters are
 * active" list of its own. The pills are computed from `filter` on every render,
 * so the row cannot disagree with what the engine actually filters on. That is
 * the F 331 rule applied to a new surface: the row READS the state, it does not
 * hold it.
 *
 * WHY A POPOVER AND NOT AN INLINE CHIP MENU
 * -----------------------------------------
 * The filter semantics are per-property multi-select across a database of
 * arbitrary shape (F 367 fixed a grouped multi-select crash). Rendering that as
 * inline rows for every property would grow without bound. `+ Filter` opens the
 * popover the popover was, unchanged — and the row stays a fixed height whether
 * the database has two filterable properties or twenty.
 *
 * THE ABSENCE IS SPELLED OUT, BECAUSE IT IS THE POINT
 * --------------------------------------------------
 * With no active filters this renders `+ Filter` and nothing else. It does NOT
 * render placeholder chips. An empty row that looks populated is worse than an
 * empty row, and a row of greyed-out "not filtering" pills is a lie about state
 * the operator did not create.
 */
import React from 'react';
import { ActionIcon, Badge, Box, Group, Pill, Popover, Stack, Text, Tooltip } from '@mantine/core';
import { IconFilter } from '@tabler/icons-react';
import type { DiscoveredProperty, PropertyFilter } from './calendarConfig';

/** One filter, flattened for display: which property, which values. */
export interface ActiveFilter {
  propertyId: string;
  propertyName: string;
  /** Human-readable option names. Never option ids — F 375 exists to be legible. */
  valueNames: string[];
  /** True when every value of the property is selected (an "is empty" style filter). */
  isTotal: boolean;
}

export interface FilterRowProps {
  /** The live filter. Read-only to this component. */
  filter: PropertyFilter;
  onChange: (next: PropertyFilter | ((prev: PropertyFilter) => PropertyFilter)) => void;
  /** Every filterable property, from `discoverProperties`. */
  properties: DiscoveredProperty[];
  /** The popover body — the same rows the old funnel icon rendered. */
  renderPopoverContent: () => React.ReactNode;
  activeCount: number;
  onClearAll: () => void;
}

/**
 * Flatten `PropertyFilter` into displayable pills.
 *
 * An option id that no longer resolves to a name falls back to the raw id rather
 * than disappearing: a filter the engine is still applying must be visible, even
 * if the option it names was deleted since. Rendering nothing there would hide
 * an active filter behind a missing label.
 */
export function buildActiveFilters(
  filter: PropertyFilter,
  properties: DiscoveredProperty[],
): ActiveFilter[] {
  const out: ActiveFilter[] = [];
  for (const [propertyId, ids] of Object.entries(filter ?? {})) {
    if (!ids || ids.length === 0) continue;
    const prop = properties.find((p) => p.id === propertyId);
    const optionById = new Map((prop?.options ?? []).map((o) => [String(o.id), o]));
    const valueNames = ids.map((id) => optionById.get(String(id))?.name ?? String(id));
    out.push({
      propertyId,
      propertyName: prop?.name ?? propertyId,
      valueNames,
      isTotal: prop ? ids.length >= prop.options.length && prop.options.length > 0 : false,
    });
  }
  return out;
}

/** The row itself. Exported so DatabaseView can place it directly under the tabs. */
export const FilterRow: React.FC<FilterRowProps> = ({
  filter,
  onChange,
  properties,
  renderPopoverContent,
  activeCount,
  onClearAll,
}) => {
  const [opened, setOpened] = React.useState(false);
  const active = React.useMemo(() => buildActiveFilters(filter, properties), [filter, properties]);

  /** Remove ONE property's filter. The other filters stay exactly as they were. */
  const removeFilter = React.useCallback(
    (propertyId: string) => {
      onChange((prev) => {
        const next = { ...prev };
        delete next[propertyId];
        return next;
      });
    },
    [onChange],
  );

  return (
    <Box
      data-fc-filter-row=""
      data-fc-filter-count={active.length}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        // Notion's behaviour: many filters scroll, they never collapse into a
        // "2 more..." dropdown. The operator asked for this explicitly.
        overflowX: 'auto',
        overflowY: 'hidden',
        padding: '6px 8px',
        minHeight: 36,
        borderBottom: '1px solid var(--fc-border-color, rgba(0,0,0,0.12))',
        scrollbarWidth: 'thin',
      }}
    >
      {active.length === 0 && (
        <Text size="xs" c="dimmed" data-fc-filter-empty="">
          No filters applied
        </Text>
      )}

      {active.map((f) => (
        <Pill
          key={f.propertyId}
          data-fc-filter-chip={f.propertyId}
          withRemoveButton
          onRemove={() => removeFilter(f.propertyId)}
          // Label the remove control so it is reachable by name, not just by ×.
          removeButtonProps={{ 'aria-label': `Remove ${f.propertyName} filter` } as never}
          size="sm"
          radius="sm"
          styles={{ root: { flexShrink: 0 } }}
        >
          {f.isTotal ? `${f.propertyName} is any` : `${f.propertyName} is ${f.valueNames.join(', ')}`}
        </Pill>
      ))}

      {activeCount > 0 && (
        <Badge
          variant="light"
          color="gray"
          size="sm"
          onClick={onClearAll}
          data-fc-filter-clear=""
          style={{ cursor: 'pointer', flexShrink: 0 }}
        >
          Clear all
        </Badge>
      )}

      {/* The popover, moved here from the toolbar's funnel icon. Body unchanged. */}
      <Popover
        position="bottom-start"
        width={360}
        shadow="md"
        withArrow
        opened={opened}
        onChange={setOpened}
      >
        <Popover.Target>
          <Tooltip label="Add a filter">
            <Pill
              data-fc-filter-add=""
              size="sm"
              radius="sm"
              onClick={() => setOpened((o) => !o)}
              style={{ cursor: 'pointer', flexShrink: 0 }}
            >
              <Group gap={4} wrap="nowrap">
                <IconFilter size={12} />
                <span>Filter</span>
              </Group>
            </Pill>
          </Tooltip>
        </Popover.Target>
        <Popover.Dropdown p="md">
          <Stack gap="sm">
            <Group justify="space-between" align="center">
              <Text fw={600} size="sm">Filter by property</Text>
              {activeCount > 0 && (
                <Text
                  component="button"
                  size="xs"
                  c="blue"
                  onClick={onClearAll}
                  data-fc-filter-clear-in-popover=""
                  style={{ background: 'none', border: 0, cursor: 'pointer', padding: 0 }}
                >
                  Clear
                </Text>
              )}
            </Group>
            {renderPopoverContent()}
          </Stack>
        </Popover.Dropdown>
      </Popover>
    </Box>
  );
};

export default FilterRow;